<script setup lang="ts">
import { getHumanizedDuration, getFormattedTime } from '@libs/utils';
import {
  AppWhiteDot,
  AppSvgIcon,
  AppImagesLoader,
  AppLazyLoadComponent,
} from '@libs/ui';

import type { FerryDetailsModalData } from './FerryDetailsModal.model';

interface FerrySegmentProps {
  ferryData: FerryDetailsModalData;
  imageHandler: (src: string) => void;
}

const props = defineProps<FerrySegmentProps>();

function getLogoUrls(): string[] {
  const urls: string[] = [];
  const logo = props.ferryData.segment[0]?.carrier?.logo;
  if (logo && !logo.includes('kiwi')) {
    urls.push(logo);
  }
  return urls;
}
</script>

<template>
  <div class="ferry-segment">
    <div class="ferry-segment-detailed-info">
      <div class="timeline">
        <div>
          <AppWhiteDot :size="8" />
        </div>

        <div>
          <AppWhiteDot :size="8" />
        </div>
      </div>

      <div class="ferry-segment-details">
        <div class="departure-summary">
          <span class="transport-time">
            {{ getFormattedTime(props.ferryData.departureDate) }}
          </span>
          <span class="transport-text">
            {{ props.ferryData.departureText }}
          </span>
        </div>

        <div class="route-summary">
          <div class="route-details">
            <span v-if="props.ferryData.segment[0]?.duration">
              {{
                getHumanizedDuration(
                  props.ferryData.segment[0]?.duration,
                  'minute'
                )
              }}
            </span>
            <div class="carrier">
              <AppLazyLoadComponent class="carrier-logo">
                <AppImagesLoader
                  :urls="getLogoUrls()"
                  :alt="$t('a11y.ferry_carrier_logo')"
                  img-type="contain"
                  @images-loader-error="props.imageHandler"
                >
                  <AppSvgIcon name="ferry" size="18" />
                </AppImagesLoader>
              </AppLazyLoadComponent>
              <span class="carrier-name">
                {{ props.ferryData.segment[0]?.carrierAndTransportationName }}
              </span>
            </div>
          </div>
          <div class="route-info">
            <div
              v-for="item in props.ferryData.segment[0]?.routeInfo"
              :key="item.title"
              class="route-info-wrapper"
            >
              <span class="title">{{ item.title }}</span>
              <span v-if="typeof item.content === 'string'">
                {{ item.content }}
              </span>
              <span v-for="content in item.content" v-else :key="content">
                {{ content }}
              </span>
            </div>
          </div>
        </div>

        <div class="arrival-summary">
          <span class="transport-time">
            {{ getFormattedTime(props.ferryData.arrivalDate) }}
          </span>
          <span class="transport-text">
            {{ props.ferryData.arrivalText }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ferry-segment {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-32);
  margin-bottom: var(--spacing-24);

  .ferry-segment-detailed-info {
    display: flex;
    column-gap: 10px;
    padding: var(--spacing-16) var(--spacing-16) var(--spacing-16) 0;

    .timeline {
      display: flex;
      width: 14px;
      padding: 3px 0px;

      background-color: var(--color-neutral-30);
      border-radius: var(--border-radius-30);

      flex-flow: column;
      justify-content: space-between;
      align-items: center;
    }

    .ferry-segment-details {
      display: flex;
      flex-flow: column;
      gap: var(--spacing-24);
      .departure-summary,
      .arrival-summary {
        display: flex;
        flex-direction: column;
        .transport-time {
          @include font-label-xxl-bold;
        }

        .transport-text {
          color: var(--color-neutral-70);
          @include font-label-l-light;
        }
      }

      .route-summary {
        display: flex;
        flex-direction: column;
        gap: var(--Spacing-spacing-16, 16px);
        flex: 1 0 0;
        margin: var(--spacing-12) 0 var(--spacing-16) 0;
        color: var(--color-neutral-70);

        .route-details {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: var(--spacing-16);
          @include font-label-l-light;

          .carrier {
            display: flex;
            gap: var(--spacing-8);
            align-items: center;
            .carrier-logo {
              width: 100%;
              max-width: var(--spacing-48);
              height: 100%;
              max-height: var(--spacing-48);
            }
            .carrier-name {
              white-space: nowrap;
            }
          }
        }

        .route-info {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-12);
          @include font-label-l-light;
          .route-info-wrapper {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-8);
            .title {
              @include font-label-l-bold;
            }
          }
        }
      }
    }
  }
}
</style>
